import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useBodyScrollLock, useModalFocusTrap } from '../hooks/useModalFocusTrap'
import { PrimaryButton } from './ui'
import { CLUB4X4_SELF_SERVICE_HREF } from '../claims/contactLinks'
import { isDevMode } from '../utils/devMode'
import { usePortalAuth } from '../auth/authContexts'

/** Idle time before showing "Are you still there?" (ms). */
const INACTIVITY_IDLE_MS = 5 * 60 * 1000
/** Time user has to respond before we log them out (ms). */
const WARNING_COUNTDOWN_MS = 30 * 1000
/** Throttle activity handler so we don't reset on every mousemove (ms). */
const ACTIVITY_THROTTLE_MS = 1000

const PUBLIC_PATHS = ['/', '/signin', '/register', '/auth/redirect']

export default function InactivityGuard() {
  const { isAuthenticated, logout } = usePortalAuth()
  const location = useLocation()
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const [showModal, setShowModal] = useState(false)
  const [countdownSecs, setCountdownSecs] = useState(WARNING_COUNTDOWN_MS / 1000)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastActivityRef = useRef(Date.now())
  const logoutStartedRef = useRef(false)

  const isPublicPath = PUBLIC_PATHS.includes(location.pathname)
  const isLoggedIn = isAuthenticated
  const shouldGuard = isLoggedIn && !isPublicPath && !isDevMode()

  useModalFocusTrap({
    active: showModal,
    containerRef: dialogRef,
    closeOnEscape: false,
  })
  useBodyScrollLock(showModal)

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  const resetIdleTimer = useCallback(() => {
    if (!shouldGuard) return
    clearIdleTimer()
    setShowModal(false)
    clearCountdownTimer()
    setCountdownSecs(WARNING_COUNTDOWN_MS / 1000)
    lastActivityRef.current = Date.now()
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      setShowModal(true)
      setCountdownSecs(WARNING_COUNTDOWN_MS / 1000)
      const id = setInterval(() => {
        setCountdownSecs((s) => {
          if (s <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current)
              countdownTimerRef.current = null
            }
            return 0
          }
          return s - 1
        })
      }, 1000)
      countdownTimerRef.current = id
    }, INACTIVITY_IDLE_MS)
  }, [shouldGuard, clearIdleTimer, clearCountdownTimer])

  const handleLogout = useCallback(async () => {
    if (logoutStartedRef.current) return
    logoutStartedRef.current = true
    clearIdleTimer()
    clearCountdownTimer()
    setShowModal(false)
    try {
      if (isAuthenticated) {
        await logout()
      }
    } catch (err) {
      console.error('[InactivityGuard] Logout failed:', err)
      window.location.replace(CLUB4X4_SELF_SERVICE_HREF)
    }
  }, [isAuthenticated, logout, clearIdleTimer, clearCountdownTimer])

  useEffect(() => {
    if (countdownSecs === 0 && showModal) {
      handleLogout()
    }
  }, [countdownSecs, showModal, handleLogout])

  useEffect(() => {
    if (!shouldGuard) {
      clearIdleTimer()
      clearCountdownTimer()
      setShowModal(false)
      return
    }
    resetIdleTimer()
    return () => {
      clearIdleTimer()
      clearCountdownTimer()
    }
  }, [shouldGuard, location.pathname, resetIdleTimer, clearIdleTimer, clearCountdownTimer])

  useEffect(() => {
    if (!shouldGuard) return
    let throttle: ReturnType<typeof setTimeout> | null = null
    const onActivity = () => {
      if (showModal) {
        resetIdleTimer()
        return
      }
      if (throttle) return
      throttle = setTimeout(() => {
        throttle = null
        lastActivityRef.current = Date.now()
        resetIdleTimer()
      }, ACTIVITY_THROTTLE_MS)
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach((ev) => window.addEventListener(ev, onActivity))
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity))
      if (throttle) clearTimeout(throttle)
    }
  }, [shouldGuard, showModal, resetIdleTimer])

  const handleStayLoggedIn = useCallback(() => {
    resetIdleTimer()
  }, [resetIdleTimer])

  if (!showModal) return null

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-4" role="presentation">
      <div className="absolute inset-0 bg-background-overlay opacity-70" aria-hidden />
      <div
        ref={dialogRef}
        className="relative z-10 flex w-full max-w-md flex-col gap-4 rounded-2xl bg-background-primary p-6 shadow-xl"
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby="inactivity-countdown"
        aria-modal="true"
        tabIndex={-1}
      >
        <h2 id={titleId} className="font-display text-lg font-semibold text-text-primary-900">
          Are you still there?
        </h2>
        <p className="text-md text-text-secondary-700">
          You&apos;ve been inactive for a while. You&apos;ll be signed out to keep your account secure.
        </p>
        <p
          id="inactivity-countdown"
          className="text-md text-text-secondary-700"
          aria-live="assertive"
          aria-atomic="true"
        >
          Time remaining:{' '}
          <strong>
            {countdownSecs} second{countdownSecs !== 1 ? 's' : ''}
          </strong>
        </p>
        <div className="flex justify-end gap-3 pt-2">
          <PrimaryButton onClick={handleStayLoggedIn}>Yes, I&apos;m here</PrimaryButton>
        </div>
      </div>
    </div>
  )
}
